import type { ZoneMinutes } from './cardioTracking';

export type CardioCalorieInput = {
  activityType: string;
  durationMinutes: number | null;
  distanceMeters?: number | null;
  elevGainM?: number | null;
  avgHr?: number | null;
  bodyWeightKg?: number | null;
  age?: number | null;
  sex?: string | null;
  caloriesOverride?: number | null;
  appleHealthKcal?: number | null;
};

export type CardioCalorieResult = {
  kcal: number;
  source: 'met_duration' | 'hr' | 'pace_grade' | 'apple_health' | 'override';
};

const MET: Record<string, number> = {
  treadmill: 9,
  outdoor_run: 9.5,
  outdoor_walk: 3.8,
  elliptical: 5,
  stationary_bike: 7,
  spin_class: 8.5,
  outdoor_ride: 8,
  swim: 8,
  rower: 8.5,
  stair_climber: 9,
  hike: 6,
  hiit: 8,
  yoga: 3,
  pilates: 3.5,
  sports: 6,
  dance: 6,
  other_cardio: 6,
  multisport: 8,
};

function metDurationKcal(weightKg: number, minutes: number, activityType: string): number {
  const met = MET[activityType] ?? 6;
  const kcalPerMin = (met * 3.5 * weightKg) / 200;
  return Math.max(1, Math.round(kcalPerMin * minutes));
}

function hrKcal(hr: number, minutes: number, weightKg: number, age: number, female: boolean): number {
  const raw = female
    ? (-20.4022 + 0.4472 * hr - 0.1263 * weightKg + 0.074 * age) / 4.184
    : (-55.0969 + 0.6309 * hr + 0.1988 * weightKg + 0.2017 * age) / 4.184;
  return Math.max(1, Math.round(raw * minutes));
}

function acsmKcal(
  activityType: string,
  weightKg: number,
  minutes: number,
  distanceMeters: number,
  elevGainM: number | null | undefined,
): number | null {
  const speedMMin = distanceMeters / minutes;
  if (!Number.isFinite(speedMMin) || speedMMin <= 0) return null;
  const grade = elevGainM != null && distanceMeters > 0 ? Math.max(0, elevGainM / distanceMeters) : 0;
  let vo2: number;
  if (activityType === 'outdoor_walk' || activityType === 'hike') {
    vo2 = 0.1 * speedMMin + 1.8 * speedMMin * grade + 3.5;
  } else if (activityType === 'outdoor_run' || activityType === 'treadmill' || activityType === 'other_cardio') {
    vo2 = 0.2 * speedMMin + 0.9 * speedMMin * grade + 3.5;
  } else {
    return null;
  }
  return Math.max(1, Math.round(((vo2 * weightKg) / 1000) * 5 * minutes));
}

export function estimateSmartCardioCalories(input: CardioCalorieInput): CardioCalorieResult {
  if (input.caloriesOverride != null && input.caloriesOverride > 0) {
    return { kcal: Math.round(input.caloriesOverride), source: 'override' };
  }
  if (input.appleHealthKcal != null && input.appleHealthKcal > 0) {
    return { kcal: Math.round(input.appleHealthKcal), source: 'apple_health' };
  }
  const minutes =
    input.durationMinutes != null && Number.isFinite(input.durationMinutes) && input.durationMinutes > 0
      ? input.durationMinutes
      : 0;
  const weight = input.bodyWeightKg != null && input.bodyWeightKg > 0 ? input.bodyWeightKg : 70;
  const age = input.age != null && input.age > 10 ? input.age : 30;
  const female = String(input.sex ?? '').toLowerCase().startsWith('f');

  if (minutes <= 0) {
    return { kcal: metDurationKcal(weight, 25, input.activityType), source: 'met_duration' };
  }

  if (input.avgHr != null && input.avgHr >= 60 && input.avgHr <= 220) {
    return { kcal: hrKcal(input.avgHr, minutes, weight, age, female), source: 'hr' };
  }

  if (input.distanceMeters != null && input.distanceMeters > 0) {
    const acsm = acsmKcal(input.activityType, weight, minutes, input.distanceMeters, input.elevGainM);
    if (acsm != null) return { kcal: acsm, source: 'pace_grade' };
  }

  return { kcal: metDurationKcal(weight, minutes, input.activityType), source: 'met_duration' };
}

export function calorieSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'hr':
      return 'Based on heart rate';
    case 'pace_grade':
      return 'Based on pace';
    case 'apple_health':
      return 'From Apple Health';
    case 'override':
      return 'You entered this';
    default:
      return 'Estimated from time';
  }
}
